import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from './config';
import { Explain } from './Explain';
import { ProvenanceBadge, relationColor } from './trustShared';
import RetypeEdge, { RefineButton, DisputeButton, NOT_REFINABLE } from './RetypeEdge';
import { snipMarkdown } from './snip';

// An edge rendered as a votable sentence: "⟨subject⟩ ⟨relation⟩ ⟨object⟩ — agree / disagree".
// UX session decision (2026-07-03): voting is assent to a statement, not homework
// on a data row, so every edge everywhere should read as a sentence. This is the
// one renderer for that; views pass the edge and which end is "this".

// Verb conjugations for the seed relation types so sentences read as English.
// User-defined types fall back to the raw name with underscores as spaces —
// downstream features must tolerate any relation name (CLAUDE.md).
const VERBS = {
  supports: { pos: 'supports', neg: 'does not support' },
  contradicts: { pos: 'contradicts', neg: 'does not contradict' },
  related_to: { pos: 'is related to', neg: 'is not related to' },
  references: { pos: 'references', neg: 'does not reference' },
  derived_from: { pos: 'derives from', neg: 'does not derive from' },
  defines: { pos: 'defines', neg: 'does not define' },
  contains: { pos: 'contains', neg: 'does not contain' },
  is_subcategory_of: { pos: 'is a subcategory of', neg: 'is not a subcategory of' },
  tagged: { pos: 'is tagged with', neg: 'is not tagged with' },
  trusts: { pos: 'trusts', neg: 'does not trust' },
  is_human: { pos: 'is human', neg: 'is not human' },
  authored_by: { pos: 'was authored by', neg: 'was not authored by' },
  visible_to: { pos: 'is visible to', neg: 'is not visible to' },
  restates: { pos: 'restates', neg: 'does not restate' },
  // source→target: the target holds, but the source bounds it
  qualifies: { pos: 'qualifies', neg: 'does not qualify' },
  revises: { pos: 'revises', neg: 'does not revise' },
  works_toward: { pos: 'works toward', neg: 'works against' },
  reflects_on: { pos: 'reflects on', neg: 'does not reflect on' },
  // machinery relations (vector-trust phase 3): sentences between relation
  // TYPES. +1-only at the write door — dissent is a downvote, so no neg
  // form should ever render; kept for the fallback contract.
  converges_onto: { pos: 'converges onto', neg: 'does not converge onto' },
  inverse_of: { pos: 'is the inverse of', neg: 'is not the inverse of' },
  // question attachment (2026-07-28). Both render SOURCE-verb-TARGET like
  // everything else here, so the phrasing is the forward one; the relation's
  // inverse_label ("turns on", "borne on by") is what the grouped views use.
  // crux_of runs question → claim: the question is PRIOR to the claim, which
  // is the whole point of the direction — a crux named before it is answered.
  crux_of: { pos: 'is a crux for', neg: 'is not a crux for' },
  bears_on: { pos: 'bears on', neg: 'does not bear on' },
  // actor identity (2026-07-25 ruling, built 2026-07-26): class + binding
  is_a: { pos: 'is a', neg: 'is not a' },
  same_actor_as: { pos: 'is the same actor as', neg: 'is not the same actor as' },
};

export function relationPhrase(name, polarity) {
  const verb = VERBS[name];
  if (verb) return polarity === -1 ? verb.neg : verb.pos;
  const raw = name.replace(/_/g, ' ');
  return polarity === -1 ? `not: ${raw}` : raw;
}

// Cast/toggle the viewer's vote on an edge (same vote twice = withdraw).
export async function castVote(edge, polarity) {
  const res =
    edge.my_vote === polarity
      ? await apiFetch(`/api/votes/${edge.id}`, { method: 'DELETE' })
      : await apiFetch('/api/votes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ relation_instance_id: edge.id, polarity }),
        });
  // drain the body: an unread response stream is garbage-collected as an
  // aborted request, which reads as a failed vote in devtools and probes
  await res.text().catch(() => {});
  return res;
}

// M-4: dense rows show content with markdown syntax stripped, so a truncated
// edge sentence can never leave a dangling `**` where the full view renders bold.
const snip = (s, n = 120) => snipMarkdown(s, n);

function End({ id, content, focusId }) {
  if (id === focusId) return <em>this</em>;
  return (
    <Link to={`/object/${id}`} style={{ color: 'inherit' }}>
      “{snip(content)}”
    </Link>
  );
}

// edge: { id, relation_name, polarity, source_id, source_content, target_id,
//         target_content, up_votes, down_votes, my_vote }
// focusId: which end of the edge the current page is about (renders as "this").
// Voting, either style (omit both for read-only):
//   onVote(edgeId, polarity) — the view casts the vote itself, or
//   onVoted() — this component casts via castVote, then calls back for a refetch.
// user: current auth user or null — gates the buttons, counts always show.
// onRetyped: opt in to the refine gesture (RetypeEdge.js) — called after a
//   correction lands so the view refetches. System edges never offer it.
// connector: opt-in COMPACT form for surfaces where both endpoints are
//   already visible as adjacent cards (the Walk's spine, 2026-08-02 fixture
//   round 2 — the full sentence made every claim appear three times in one
//   viewport). Pass the arrow glyph pointing at the SUPPORTED card ('↑' when
//   the target renders above, '↓' below); the verb keeps its color, voting
//   and badges are unchanged, and the full sentence survives as the title.
export default function EdgeSentence({ edge, focusId, onVote, onVoted, user, actions, onRetyped, connector }) {
  const [refineOpen, setRefineOpen] = useState(false);
  // Optimistic vote echo (owner-ruled 2026-07-30): the click renders as landed
  // immediately; the refetch reconciles. Every write drops the graph cache, so
  // the round trip pays a cold rebuild (~750ms measured) — without the echo the
  // button reads as dead for that whole window. The vote itself is still the
  // server's: a failed request snaps the render back, and the effect below
  // clears the echo the moment the refetched edge arrives.
  const [optimistic, setOptimistic] = useState(null);
  const [inFlight, setInFlight] = useState(false);
  useEffect(() => {
    setOptimistic(null);
  }, [edge.my_vote, edge.up_votes, edge.down_votes]);
  const myVote = optimistic ? optimistic.myVote : edge.my_vote;
  const upVotes = optimistic ? optimistic.up : edge.up_votes;
  const downVotes = optimistic ? optimistic.down : edge.down_votes;
  const activated = upVotes + downVotes > 0;
  const votable = onVote || onVoted;
  const refinable =
    Boolean(onRetyped) &&
    Boolean(user) &&
    !edge.is_system &&
    !NOT_REFINABLE.has(edge.relation_name);

  // DISPUTE — argue WITH a connection (owner ruling 2026-08-02; edges became
  // targetable by edges). Deliberately beside refine, because the pair is the
  // distinction worth teaching at the point of use:
  //
  //   refine  = the endpoints are right, the NAME is wrong → mints a corrected
  //             edge and downvotes this one.
  //   dispute = the endpoints are fine and the LINK is what is wrong → opens the
  //             connection's own page, where it can be argued about like
  //             anything else.
  //
  // Flattening those two into one gesture is how `related_to` became a dumping
  // ground. Same gating as refine: a signed-in human, never a system edge —
  // plumbing records where content came from and there is nothing to dispute.
  const arguable = Boolean(user) && !edge.is_system && !NOT_REFINABLE.has(edge.relation_name);
  const [disputing, setDisputing] = useState(false);
  const navigate = useNavigate();
  // The handle is minted HERE, at the moment someone chooses to argue — that
  // laziness is the whole reason handles are affordable (one per argued-about
  // edge, not one per edge).
  const openDispute = async () => {
    if (disputing) return;
    setDisputing(true);
    try {
      const res = await apiFetch(`/api/relation-instances/${edge.id}/handle`, { method: 'POST' });
      if (res.ok) {
        const handle = await res.json();
        navigate(`/object/${handle.object_id}`);
      }
    } finally {
      setDisputing(false);
    }
  };
  const handleVote = async (polarity) => {
    if (onVote) return onVote(edge.id, polarity);
    // one request at a time: castVote derives POST-vs-DELETE from the edge
    // prop, which is stale until the refetch lands
    if (inFlight) return;
    const withdrawing = edge.my_vote === polarity;
    setOptimistic({
      myVote: withdrawing ? null : polarity,
      up:
        edge.up_votes +
        (polarity === 1 ? (withdrawing ? -1 : 1) : edge.my_vote === 1 ? -1 : 0),
      down:
        edge.down_votes +
        (polarity === -1 ? (withdrawing ? -1 : 1) : edge.my_vote === -1 ? -1 : 0),
    });
    setInFlight(true);
    try {
      const res = await castVote(edge, polarity);
      if (res && res.ok === false) throw new Error(`vote failed (${res.status})`);
      onVoted();
    } catch (err) {
      setOptimistic(null);
      console.error('Vote failed:', err);
    } finally {
      setInFlight(false);
    }
  };
  const voteBtn = (polarity, label) => {
    const mine = myVote === polarity;
    const count = polarity === 1 ? upVotes : downVotes;
    return (
      <button
        className="edge-vote"
        aria-pressed={mine}
        onClick={() => handleVote(polarity)}
        title={mine ? 'Click again to withdraw your vote' : `${label}: this connection ${polarity === 1 ? 'holds' : 'does not hold'}`}
        style={{
          cursor: 'pointer',
          // the blend's vote pill (BLEND_REDESIGN_SPEC.md): soft surface pill,
          // pressed = pos fill, the count in the data voice. The --edge-* vars
          // stay themeable so the Reader's scoped skin can re-ink them.
          // (size lives in app.css .edge-vote so coarse pointers can bump it)
          border: '1px solid transparent',
          borderRadius: 'var(--wv-r-pill)',
          background: mine ? 'var(--edge-vote-on, var(--wv-pos))' : 'var(--edge-vote-bg, var(--wv-surface2))',
          color: mine ? 'var(--edge-vote-on-ink, var(--wv-bg))' : 'var(--edge-ink, var(--wv-muted))',
          fontWeight: mine ? 700 : 500,
        }}
      >
        {mine ? '✓ ' : ''}{label} <span className="wv-data">{count}</span>
        {/* withdraw made VISIBLE (UI polish batch): clicking a pressed vote
            withdraws it — real epistemic machinery, since a withdrawn vote can
            deactivate an edge — and that affordance lived only in the title
            tooltip, invisible on touch. The trailing × is the app's removal
            idiom (tag chips); the whole pill stays one button. */}
        {mine && <span aria-hidden="true" style={{ marginLeft: 'var(--wv-sp-1)', opacity: 0.75 }}>×</span>}
      </button>
    );
  };

  return (
    <div
      className="trust-row"
      style={{ cursor: 'default', alignItems: 'center', flexWrap: 'wrap', gap: 'var(--wv-sp-3) var(--wv-sp-6)' }}
    >
      <span style={{ flex: 1, minWidth: connector ? 0 : 'min(260px, 100%)', fontSize: 'var(--wv-fs-ui)', lineHeight: 'var(--wv-lh)' }}>
        {connector ? (
          <strong
            style={{ color: relationColor(edge.relation_name) }}
            title={`“${snip(edge.source_content)}” ${relationPhrase(edge.relation_name, edge.polarity)} “${snip(edge.target_content)}”`}
          >
            {connector} {relationPhrase(edge.relation_name, edge.polarity)}
          </strong>
        ) : (
          <>
            <End id={edge.source_id} content={edge.source_content} focusId={focusId} />{' '}
            <strong style={{ color: relationColor(edge.relation_name) }}>
              {relationPhrase(edge.relation_name, edge.polarity)}
            </strong>{' '}
            <End id={edge.target_id} content={edge.target_content} focusId={focusId} />
          </>
        )}
      </span>
      {/* the cluster must be able to wrap internally: in narrow cards
          (Reader case panels ~300px) buttons + badge together exceed the
          card width, and flexShrink:0 pushed the badge through its border */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--wv-sp-2)', flexWrap: 'wrap', minWidth: 0 }}>
        {votable && user ? (
          <Explain concept="voting">
            <span style={{ display: 'inline-flex', gap: 'var(--wv-sp-2)' }}>
              {voteBtn(1, 'agree')}
              {voteBtn(-1, 'disagree')}
            </span>
          </Explain>
        ) : (
          activated && (
            <span className="trust-muted" style={{ fontSize: 'var(--wv-fs-sm)' }}>
              {upVotes} agree · {downVotes} disagree
            </span>
          )
        )}
        {edge.is_system ? (
          // system edges (authored_by, visible_to) are plumbing, not testimony —
          // the human/AI provenance framing doesn't apply to them
          <span className="prov-badge" style={{ background: 'var(--wv-surface2)', color: 'var(--wv-muted)', border: '1px solid var(--wv-border)' }}>
            system
          </span>
        ) : (
          <ProvenanceBadge activated={activated} upVotes={upVotes} downVotes={downVotes} />
        )}
        {refinable && (
          <RefineButton open={refineOpen} onClick={() => setRefineOpen((o) => !o)} />
        )}
        {arguable && <DisputeButton busy={disputing} onOpen={openDispute} />}
        {actions}
      </span>
      {/* the panel takes a full row of its own inside the wrapping flex box —
          the action cluster above already overflows narrow (Reader) cards */}
      {refinable && refineOpen && (
        <div style={{ flex: '0 0 100%', minWidth: 0 }}>
          <RetypeEdge
            edge={edge}
            onRetyped={onRetyped}
            onClose={() => setRefineOpen(false)}
          />
        </div>
      )}
    </div>
  );
}
